import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BugReportIcon from '@mui/icons-material/BugReport';
import DashboardIcon from '@mui/icons-material/Dashboard';
import FolderIcon from '@mui/icons-material/Folder';
import HomeIcon from '@mui/icons-material/Home';
import InsightsIcon from '@mui/icons-material/Insights';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LockIcon from '@mui/icons-material/Lock';
import MenuIcon from '@mui/icons-material/Menu';
import PermContactCalendarIcon from '@mui/icons-material/PermContactCalendar';
import PersonIcon from '@mui/icons-material/Person';
import StorageIcon from '@mui/icons-material/Storage';
import TuneIcon from '@mui/icons-material/Tune';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';

import {
    AppBar,
    Box,
    Button,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Toolbar,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const AdminNavbar = () => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [sectionsAnchorEl, setSectionsAnchorEl] = useState(null);
    const sectionsMenuOpen = Boolean(sectionsAnchorEl);
    const location = useLocation();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

    const toggleDrawer = (open) => (event) => {
        if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }
        setDrawerOpen(open);
    };

    const handleSectionsMenuClick = (event) => {
        setSectionsAnchorEl(event.currentTarget);
    };

    const handleSectionsMenuClose = () => {
        setSectionsAnchorEl(null);
    };

    const dashboardItem = {
        text: 'Dashboard',
        path: '/admin',
        icon: <DashboardIcon />,
    };

    // Matches the "Team Management" section on AdminDashboard, in the same order
    const teamManagementItems = [
        {
            text: 'User Management',
            path: '/admin/users',
            icon: <PersonIcon />,
        },
        {
            text: 'Group Management',
            path: '/admin/groups',
            icon: <PermContactCalendarIcon />,
        },
        {
            text: 'Folder Permissions',
            path: '/admin/permissions',
            icon: <LockIcon />,
        },
        {
            text: 'Donor Impact Report',
            path: '/admin/impact-report',
            icon: <AssessmentIcon />,
        },
        {
            text: 'Bug Reports',
            path: '/admin/bug-reports',
            icon: <BugReportIcon />,
        },
    ];

    // Matches the "Admin Only" section on AdminDashboard, in the same order
    const adminOnlyItems = [
        {
            text: 'Files & Folders',
            path: '/admin/mongo-files',
            icon: <FolderIcon />,
        },
        {
            text: 'Videos',
            path: '/admin/mongo-videos',
            icon: <VideoLibraryIcon />,
        },
        {
            text: 'Ops Tool',
            path: '/admin/ops-tools',
            icon: <StorageIcon />,
        },
        {
            text: 'Task tools',
            path: '/tasks/tools',
            icon: <TuneIcon />,
        },
        {
            text: 'Instrumentation',
            path: '/instrumentation',
            icon: <InsightsIcon />,
        },
    ];

    const navItems = [dashboardItem, ...teamManagementItems, ...adminOnlyItems];

    const isActive = (path) => {
        return location.pathname === path;
    };

    const drawer = (
        <Box sx={{ width: 250 }} role="presentation" onClick={toggleDrawer(false)} onKeyDown={toggleDrawer(false)}>
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AdminPanelSettingsIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography
                    variant="h6"
                    component="div"
                    sx={{ flexGrow: 1, color: 'primary.main', fontWeight: 'bold' }}
                >
                    Admin
                </Typography>
            </Box>
            <Divider />
            <List>
                {navItems.map((item) => (
                    <ListItem
                        button
                        key={item.text}
                        component={Link}
                        to={item.path}
                        selected={isActive(item.path)}
                        sx={{
                            '&.Mui-selected': {
                                backgroundColor: 'primary.light',
                                '&:hover': {
                                    backgroundColor: 'primary.light',
                                },
                            },
                        }}
                    >
                        <ListItemIcon sx={{ color: isActive(item.path) ? 'primary.main' : 'inherit' }}>
                            {item.icon}
                        </ListItemIcon>
                        <ListItemText
                            primary={item.text}
                            sx={{
                                '& .MuiListItemText-primary': {
                                    fontWeight: isActive(item.path) ? 'bold' : 'normal',
                                    color: isActive(item.path) ? 'primary.main' : 'inherit',
                                },
                            }}
                        />
                    </ListItem>
                ))}
            </List>
            <Divider />
            <List>
                <ListItem button component={Link} to="/">
                    <ListItemIcon>
                        <HomeIcon />
                    </ListItemIcon>
                    <ListItemText primary="Back to Main App" />
                </ListItem>
            </List>
        </Box>
    );

    return (
        <Box>
            <AppBar
                position="static"
                sx={{
                    backgroundColor: theme.palette.admin?.navbarBg || theme.palette.header?.bg,
                    backgroundImage: 'none',
                    borderRadius: 0,
                    width: '100%',
                    padding: 0,
                    margin: 0,
                }}
            >
                <Toolbar>
                    {isSmallScreen && (
                        <IconButton
                            size="large"
                            edge="start"
                            color="inherit"
                            aria-label="menu"
                            sx={{ mr: 2 }}
                            onClick={toggleDrawer(true)}
                        >
                            <MenuIcon />
                        </IconButton>
                    )}

                    <Typography
                        variant="h6"
                        component="div"
                        sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}
                    >
                        <AdminPanelSettingsIcon sx={{ mr: 1 }} />
                        Admin
                    </Typography>

                    {!isSmallScreen && (
                        <>
                            <Button
                                id="admin-sections-button"
                                variant="text"
                                color="inherit"
                                startIcon={<MenuIcon />}
                                endIcon={<KeyboardArrowDownIcon fontSize="small" />}
                                size="small"
                                sx={{
                                    textTransform: 'none',
                                    fontSize: '0.85rem',
                                    mr: 2,
                                    backgroundColor: sectionsMenuOpen ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.25)',
                                    },
                                }}
                                onClick={handleSectionsMenuClick}
                                aria-controls={sectionsMenuOpen ? 'admin-sections-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={sectionsMenuOpen ? 'true' : undefined}
                            >
                                Sections
                            </Button>
                            <Menu
                                id="admin-sections-menu"
                                anchorEl={sectionsAnchorEl}
                                open={sectionsMenuOpen}
                                onClose={handleSectionsMenuClose}
                                anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                                transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                            >
                                <MenuItem
                                    component={Link}
                                    to={dashboardItem.path}
                                    selected={isActive(dashboardItem.path)}
                                    onClick={handleSectionsMenuClose}
                                    sx={{ minWidth: '220px', pl: 3 }}
                                >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 'auto',
                                            mr: 1.5,
                                            color: isActive(dashboardItem.path) ? 'primary.main' : 'inherit',
                                        }}
                                    >
                                        {dashboardItem.icon}
                                    </ListItemIcon>
                                    {dashboardItem.text}
                                </MenuItem>
                                <Divider />

                                <Box
                                    sx={{
                                        borderLeft: '3px solid',
                                        borderColor: 'primary.main',
                                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                        borderRadius: '0 4px 4px 0',
                                        px: 2,
                                        py: 0.5,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{ fontWeight: 'bold', color: 'primary.main', letterSpacing: '0.05em' }}
                                    >
                                        TEAM MANAGEMENT
                                    </Typography>
                                </Box>
                                <Divider />
                                {teamManagementItems.map((item) => (
                                    <MenuItem
                                        key={item.text}
                                        component={Link}
                                        to={item.path}
                                        selected={isActive(item.path)}
                                        onClick={handleSectionsMenuClose}
                                        sx={{ minWidth: '220px', pl: 3 }}
                                    >
                                        <ListItemIcon
                                            sx={{
                                                minWidth: 'auto',
                                                mr: 1.5,
                                                color: isActive(item.path) ? 'primary.main' : 'inherit',
                                            }}
                                        >
                                            {item.icon}
                                        </ListItemIcon>
                                        {item.text}
                                    </MenuItem>
                                ))}

                                <Box
                                    sx={{
                                        borderLeft: '3px solid',
                                        borderColor: 'secondary.main',
                                        backgroundColor: 'rgba(156, 39, 176, 0.08)',
                                        borderRadius: '0 4px 4px 0',
                                        px: 2,
                                        py: 0.5,
                                        mt: 0.5,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{ fontWeight: 'bold', color: 'secondary.main', letterSpacing: '0.05em' }}
                                    >
                                        ADMIN ONLY
                                    </Typography>
                                </Box>
                                <Divider />
                                {adminOnlyItems.map((item) => (
                                    <MenuItem
                                        key={item.text}
                                        component={Link}
                                        to={item.path}
                                        selected={isActive(item.path)}
                                        onClick={handleSectionsMenuClose}
                                        sx={{ minWidth: '220px', pl: 3 }}
                                    >
                                        <ListItemIcon
                                            sx={{
                                                minWidth: 'auto',
                                                mr: 1.5,
                                                color: isActive(item.path) ? 'primary.main' : 'inherit',
                                            }}
                                        >
                                            {item.icon}
                                        </ListItemIcon>
                                        {item.text}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </>
                    )}

                    <Tooltip title="Return to main application">
                        <Button color="inherit" component={Link} to="/" startIcon={<ArrowBackIcon />}>
                            Main App
                        </Button>
                    </Tooltip>
                </Toolbar>
            </AppBar>
            <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)}>
                {drawer}
            </Drawer>
        </Box>
    );
};

export default AdminNavbar;
